"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { Check, Copy, X } from "lucide-react";
import { clockParts, copyText, offerLive, useNow, useOffer } from "@/lib/promo";

/**
 * The discount offer's bar above the menu (admin → Discount offer). Computers and tablets only (phones get the
 * popup instead), one line: the offer's label, the admin's message, the code, a live countdown, "Claim offer" and a
 * close button. Shown only between the offer's start and end; once closed it stays closed for that offer.
 */
export function LaunchOfferBar() {
  const offer = useOffer();
  const now = useNow(!!offer);
  const [hidden, setHidden] = useState(true);
  const [copied, setCopied] = useState(false);
  const key = offer ? `rb-offer-bar-${offer.id}` : "";

  useEffect(() => {
    if (!key) return;
    try {
      setHidden(window.localStorage.getItem(key) === "closed");
    } catch {
      setHidden(false);
    }
  }, [key]);

  if (!offer || hidden || !offerLive(offer, now) || now == null) return null;
  const left = clockParts(Date.parse(offer.ends_at) - now);
  const code = offer.codes[0]?.code;

  const close = () => {
    setHidden(true);
    try {
      window.localStorage.setItem(key, "closed");
    } catch {
      // closed for this page only
    }
  };

  return (
    <div className="rb-offerbar relative z-[101] hidden overflow-hidden text-white md:block" role="region" aria-label={offer.label}>
      <style>{`
        .rb-offerbar { background: linear-gradient(100deg, #120d2e 0%, #24186a 30%, #4b2fd0 62%, #a62d86 100%); }
        .rb-offerbar::before { content: ""; position: absolute; inset: 0; background: linear-gradient(105deg, transparent 35%, rgba(255,255,255,.16) 50%, transparent 65%); transform: translateX(-100%); animation: rb-offer-sheen 6s ease-in-out infinite 1.2s; pointer-events: none }
        .rb-offerbar::after { content: ""; position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,.18) 1px, transparent 1px); background-size: 18px 18px; mask-image: linear-gradient(90deg, transparent, #000 30%, #000 70%, transparent); opacity: .35; pointer-events: none }
        @keyframes rb-offer-sheen { 0% { transform: translateX(-100%) } 45%, 100% { transform: translateX(100%) } }
        @keyframes rb-offer-ping { 0% { transform: scale(1); opacity: .7 } 80%, 100% { transform: scale(2.4); opacity: 0 } }
        .rb-offer-ping { animation: rb-offer-ping 1.8s cubic-bezier(0,0,.2,1) infinite }
        @media (prefers-reduced-motion: reduce) { .rb-offerbar::before, .rb-offer-ping { animation: none } }
      `}</style>
      <div className="relative mx-auto flex h-11 max-w-[1400px] items-center gap-3 whitespace-nowrap px-4 text-[0.82rem] lg:px-6">
        <span className="hidden flex-shrink-0 items-center gap-2 rounded-full bg-white/12 py-1 pl-2 pr-3 text-[0.66rem] lg:flex font-extrabold uppercase tracking-[0.1em] ring-1 ring-white/25">
          <span className="relative flex h-2 w-2">
            <span className="rb-offer-ping absolute inline-flex h-full w-full rounded-full bg-[#FF8FCF]" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FF8FCF]" />
          </span>
          {offer.label}
        </span>

        <p className="min-w-0 flex-1 truncate font-semibold text-white/95" title={offer.bar_text}>
          {offer.bar_text}
        </p>

        {code && (
          <button
            type="button"
            onClick={async () => {
              if (await copyText(code)) {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1600);
              }
            }}
            className="hidden flex-shrink-0 items-center gap-1.5 rounded-lg border border-dashed border-white/45 bg-white/[0.07] px-2.5 py-1 font-mono text-[0.76rem] font-bold tracking-[0.08em] transition hover:border-white hover:bg-white/15 lg:flex"
            title="Copy the code"
          >
            {code}
            {copied ? <Check size={13} /> : <Copy size={12} className="opacity-70" />}
          </button>
        )}

        <span className="hidden flex-shrink-0 items-center gap-1 tabular-nums xl:flex" aria-label="Time left">
          <span className="mr-1 text-[0.7rem] font-bold uppercase tracking-[0.08em] text-white/60">Ends in</span>
          {left.d > 0 && <Unit v={String(left.d)} u="d" />}
          <Unit v={left.h} u="h" />
          <Unit v={left.m} u="m" />
          <Unit v={left.s} u="s" />
        </span>

        <Link
          href="/pricing"
          className="group flex flex-shrink-0 items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-[0.78rem] font-extrabold text-[#2a1b7a] shadow-[0_8px_20px_-8px_rgba(0,0,0,0.6)] transition hover:-translate-y-px hover:shadow-[0_12px_24px_-8px_rgba(0,0,0,0.7)]"
        >
          Claim offer <span className="transition-transform group-hover:translate-x-0.5" aria-hidden>→</span>
        </Link>
        <button
          type="button"
          onClick={close}
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-white/70 transition hover:bg-white/15 hover:text-white"
          aria-label="Close the offer bar"
        >
          <X size={15} />
        </button>
      </div>
    </div>
  );
}

function Unit({ v, u }: { v: string; u: string }) {
  return (
    <span className="inline-flex items-baseline rounded-md bg-black/25 px-1.5 py-0.5 ring-1 ring-white/10">
      <b className="text-[0.8rem] font-extrabold">{v}</b>
      <span className="ml-px text-[0.62rem] font-bold text-white/60">{u}</span>
    </span>
  );
}
